import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SignUpProfileFormData } from '../../../../types/registration';
import { BRAZILIAN_STATES } from '../../../../constants/brazilianStates';
import { CALLING_CODES, CODE_TO_ISO, extractCallingCode } from '../../../../constants/callingCodes';
import { useCountries } from '../../../../constants/countries';
import { maskCpf, maskPhoneByCountry, unmaskPhone, phoneMaxLength, phonePlaceholder, maskPostalCode, postalCodeMaxLength, postalCodePlaceholder, maskHeight, maskWeight, maskInstagram } from '../../../../utils/masks';
import { requiresBrazilianCpf } from '../../../../utils/validation';
import FormInput from '../../../../components/common/FormInput/FormInput';
import SearchableSelect, { SelectOption } from '../../../../components/common/SearchableSelect/SearchableSelect';
import BooleanRadio from '../../../../components/common/BooleanRadio/BooleanRadio';
import type { CoachOption } from '../../../../services/CoachService';
import CountryFlag from '../../../../components/common/CountryFlag/CountryFlag';
import styles from '../StudentRegister.module.css';

interface SignUpProfileStepProps {
  data: SignUpProfileFormData;
  errors: Record<string, string>;
  onFieldChange: (field: keyof SignUpProfileFormData, value: string) => void;
  onCepBlur: () => void;
  isForeignStudent?: boolean;
  isTradeAgreement?: boolean;
  onForeignStudentChange?: (value: boolean) => void;
  onTradeAgreementChange?: (value: boolean) => void;
  editMode?: boolean;
  /** When true, name/email fields are rendered in the left-column sidebar instead */
  hideSidebarFields?: boolean;
  readOnly?: boolean;
  /** When set, overrides `readOnly` for the Professora field only. */
  coachReadOnly?: boolean;
  loadingCep?: boolean;
  coachOptions?: CoachOption[];
  loadingCoachOptions?: boolean;
  titleAction?: React.ReactNode;
}

const ADDRESS_FORM_FIELDS = ['cep', 'country', 'city', 'state', 'street', 'number', 'complement', 'neighborhood'] as const;

const SignUpProfileStep: React.FC<SignUpProfileStepProps> = ({
  data,
  errors,
  onFieldChange,
  onCepBlur,
  isForeignStudent = false,
  isTradeAgreement = false,
  onForeignStudentChange,
  onTradeAgreementChange,
  editMode = false,
  hideSidebarFields = false,
  readOnly = false,
  coachReadOnly,
  loadingCep = false,
  coachOptions = [],
  loadingCoachOptions = false,
  titleAction,
}) => {
  const isCoachFieldReadOnly = coachReadOnly ?? readOnly;
  const isBrazil = !data.country || data.country === 'BR';
  const requiresCpf = requiresBrazilianCpf(data.country, isForeignStudent);
  const cpfDigits = (data.cpf?.replace(/\D/g, '') ?? '').length;
  const isCpfWarning = cpfDigits > 0 && cpfDigits !== 11;
  const filledAddressFields = ADDRESS_FORM_FIELDS.filter((f) => Boolean(data[f])).length;
  const isAddressWarning = filledAddressFields < 4;

  const { countries: rawCountryOptions, loading: countriesLoading } = useCountries();
  const countryOptions = useMemo(
    () => [...rawCountryOptions].sort((a, b) => a.label.localeCompare(b.label, 'pt')),
    [rawCountryOptions]
  );

  const [cities, setCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  /* Reset state/city/cep when country changes */
  const prevCountry = useRef(data.country);
  useEffect(() => {
    if (prevCountry.current !== data.country) {
      prevCountry.current = data.country;
      onFieldChange('state', '');
      onFieldChange('city', '');
      onFieldChange('cep', '');
    }
  }, [data.country, onFieldChange]);

  /* Re-mask phone when calling code changes */
  const prevCallingCode = useRef(data.callingCode);
  useEffect(() => {
    if (prevCallingCode.current !== data.callingCode) {
      prevCallingCode.current = data.callingCode;
      if (data.phone) {
        const raw = unmaskPhone(data.phone);
        onFieldChange('phone', maskPhoneByCountry(raw, data.callingCode || '55'));
      }
    }
  }, [data.callingCode, data.phone, onFieldChange]);

  useEffect(() => {
    if (!isBrazil || !data.state) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setCitiesLoading(true);
    fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${data.state}/municipios?orderBy=nome`
    )
      .then((r) => r.json())
      .then((list: { nome: string }[]) => {
        if (!cancelled) {
          setCities(list.map((m) => m.nome));
          setCitiesLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setCitiesLoading(false); });
    return () => { cancelled = true; };
  }, [data.state, isBrazil]);

  const stateOptions = useMemo<SelectOption[]>(
    () => BRAZILIAN_STATES.map((s) => ({ value: s.value, label: s.label })),
    []
  );

  const cityOptions = useMemo<SelectOption[]>(
    () => cities.map((c) => ({ value: c, label: c })),
    [cities]
  );

  const coachSelectOptions = useMemo<SelectOption[]>(
    () => coachOptions.map((coach) => ({ value: String(coach.id), label: coach.label })),
    [coachOptions],
  );

  const handleMaskedChange = (field: keyof SignUpProfileFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    const masks: Partial<Record<keyof SignUpProfileFormData, (v: string) => string>> = {
      cpf: maskCpf,
      phone: (v) => maskPhoneByCountry(v, data.callingCode || '55'),
      cep: (v) => maskPostalCode(v, data.country || 'BR'),
      height: maskHeight,
      weight: maskWeight,
      instagram: maskInstagram,
    };
    const masked = masks[field] ? masks[field]!(value) : value;
    onFieldChange(field, masked);
  };

  const computedAge = useMemo(() => {
    if (!data.birthDate) return null;
    const birth = new Date(data.birthDate);
    if (isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  }, [data.birthDate]);

  const callingCodeOptions = useMemo(
    () => [...CALLING_CODES]
      .sort((a, b) => a.label.localeCompare(b.label, 'pt'))
      .map((c) => ({ value: c.value, label: c.label })),
    []
  );

  return (
    <div className={styles.card}>
      {/* ── Informações básicas ── */}
      <div>
        <div className={styles.sectionHeaderRow}>
          <h2 className={styles.columnTitle}>Informações básicas</h2>
          {titleAction}
        </div>
        <div className={styles.formGrid2Col}>
          <FormInput
            label="Nome Completo"
            required
            name="fullName"
            value={data.name}
            onChange={handleMaskedChange('name')}
            placeholder="Inserir nome completo"
            maxLength={100}
            error={errors.name}
            readOnly={readOnly}
          />

          <div className={styles.birthDateRow}>
            <FormInput
              label="Data de nascimento"
              type="date"
              name="birthDate"
              value={data.birthDate}
              onChange={handleMaskedChange('birthDate')}
              readOnly={readOnly}
            />
            <FormInput
              label="Idade"
              name="age"
              value={computedAge !== null ? `${computedAge} anos` : ''}
              placeholder="—"
              readOnly
              tabIndex={-1}
              containerClassName={styles.ageField}
            />
          </div>

          <FormInput
            label="CPF"
            name="cpf"
            required={requiresCpf}
            value={data.cpf}
            onChange={handleMaskedChange('cpf')}
            placeholder="Inserir número do CPF"
            maxLength={14}
            error={errors.cpf}
            warning={isCpfWarning}
            warningTitle="CPF inválido ou incompleto"
            readOnly={readOnly}
          />

          <FormInput
            label="Data de adesão"
            type="date"
            name="joinedAt"
            value={data.joinedAt}
            onChange={handleMaskedChange('joinedAt')}
            readOnly={readOnly}
          />

          {!hideSidebarFields && (
            <FormInput
              label="E-mail"
              type="email"
              name="email"
              value={data.email}
              onChange={handleMaskedChange('email')}
              placeholder="email@exemplo.com"
              maxLength={255}
              error={errors.email}
              readOnly={readOnly}
            />
          )}

          <FormInput
            label="Instagram"
            name="instagram"
            value={data.instagram}
            onChange={handleMaskedChange('instagram')}
            placeholder="@usuario"
            maxLength={50}
            readOnly={readOnly}
          />

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Professora</label>
            <SearchableSelect
              value={data.coachId}
              onChange={(value) => onFieldChange('coachId', value)}
              options={coachSelectOptions}
              placeholder={loadingCoachOptions ? 'Carregando professoras...' : 'Selecione a professora'}
              searchPlaceholder="Pesquisar"
              noResultsText="Nenhuma professora encontrada"
              readOnly={isCoachFieldReadOnly}
            />
            {errors.coachId && <span className={styles.errorText}>{errors.coachId}</span>}
          </div>

          {hideSidebarFields && onForeignStudentChange && onTradeAgreementChange && (
            <>
              <div className={styles.inlineOptionRow}>
                <label className={styles.label}>Aluna estrangeira</label>
                <BooleanRadio
                  name="isForeignStudent"
                  value={isForeignStudent}
                  onChange={onForeignStudentChange}
                  disabled={readOnly}
                />
              </div>

              <div className={styles.inlineOptionRow}>
                <label className={styles.label}>Permuta</label>
                <BooleanRadio
                  name="isTradeAgreement"
                  value={isTradeAgreement}
                  onChange={onTradeAgreementChange}
                  disabled={readOnly}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Contatos ── */}
      <div>
        <h2 className={styles.columnTitle}>Contatos</h2>
        <div className={styles.formGrid2Col}>
          <div className={styles.phoneRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Código</label>
              <SearchableSelect
                value={data.callingCode}
                onChange={(v) => onFieldChange('callingCode', v)}
                options={callingCodeOptions}
                placeholder="+55"
                searchPlaceholder="Pesquisar"
                triggerFormat={(opt) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <CountryFlag code={CODE_TO_ISO[opt.value] || ''} size={16} /> +{extractCallingCode(opt.value)}
                  </span>
                )}
                optionFormat={(opt) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <CountryFlag code={CODE_TO_ISO[opt.value] || ''} size={16} /> {opt.label}
                  </span>
                )}
                noResultsText="Nenhum código encontrado"
                readOnly={readOnly}
              />
            </div>
            <FormInput
              label="Celular com DDD"
              name="phone"
              value={data.phone}
              onChange={handleMaskedChange('phone')}
              placeholder={phonePlaceholder(data.callingCode || '55')}
              maxLength={phoneMaxLength(data.callingCode || '55')}
              error={errors.phone}
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>

      {/* ── Endereço ── */}
      <div>
        <h2 className={styles.columnTitle}>
          Endereço
          {isAddressWarning && (
            <span className={styles.sectionWarningIcon} title="Endereço incompleto (menos de 4 campos preenchidos)">
              warning
            </span>
          )}
        </h2>
        <div className={styles.formGrid2Col}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>País</label>
            <SearchableSelect
              value={data.country}
              onChange={(v) => onFieldChange('country', v)}
              options={countryOptions}
              placeholder="Selecione o país..."
              searchPlaceholder="Pesquisar"
              loading={countriesLoading}
              triggerFormat={(opt) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <CountryFlag code={opt.value} size={16} /> {opt.label}
                </span>
              )}
              optionFormat={(opt) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <CountryFlag code={opt.value} size={16} /> {opt.label}
                </span>
              )}
              noResultsText="País não encontrado"
              readOnly={readOnly}
            />
          </div>

          <FormInput
            label="CEP / Código Postal"
            name="cep"
            value={data.cep}
            onChange={handleMaskedChange('cep')}
            onBlur={onCepBlur}
            placeholder={postalCodePlaceholder(data.country || 'BR')}
            maxLength={postalCodeMaxLength(data.country || 'BR')}
            error={errors.cep}
            readOnly={readOnly}
            loading={loadingCep}
          />

          <FormInput
            label="Rua"
            name="street"
            value={data.street}
            onChange={handleMaskedChange('street')}
            placeholder="Nome da rua"
            readOnly={readOnly}
          />

          <FormInput
            label="Número"
            name="number"
            value={data.number}
            onChange={handleMaskedChange('number')}
            placeholder="123"
            readOnly={readOnly}
          />

          <FormInput
            label="Complemento"
            name="complement"
            value={data.complement}
            onChange={handleMaskedChange('complement')}
            placeholder="Apto, Bloco..."
            readOnly={readOnly}
          />

          <FormInput
            label="Bairro"
            name="neighborhood"
            value={data.neighborhood}
            onChange={handleMaskedChange('neighborhood')}
            placeholder="Bairro"
            readOnly={readOnly}
          />

          {isBrazil ? (
            <div className={styles.fieldGroup}>
              <label className={styles.label}>UF</label>
              <SearchableSelect
                value={data.state}
                onChange={(v) => {
                  onFieldChange('state', v);
                  onFieldChange('city', '');
                }}
                options={stateOptions}
                placeholder="Selecione o estado..."
                searchPlaceholder="Pesquisar"
                noResultsText="Estado não encontrado"
                readOnly={readOnly}
              />
            </div>
          ) : (
            <FormInput
              label="Estado / Província"
              name="state"
              value={data.state}
              onChange={handleMaskedChange('state')}
              placeholder="Estado ou província"
              readOnly={readOnly}
            />
          )}

          {isBrazil ? (
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Cidade</label>
              <SearchableSelect
                value={data.city}
                onChange={(v) => onFieldChange('city', v)}
                options={cityOptions}
                placeholder={data.state ? 'Selecione a cidade...' : 'Selecione o estado primeiro'}
                searchPlaceholder="Pesquisar"
                disabled={!data.state}
                loading={citiesLoading}
                noResultsText="Cidade não encontrada"
                readOnly={readOnly}
              />
            </div>
          ) : (
            <FormInput
              label="Cidade"
              name="city"
              value={data.city}
              onChange={handleMaskedChange('city')}
              placeholder="Cidade"
              readOnly={readOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUpProfileStep;
